import React, { useState, useRef } from 'react';
import { GripVertical, X, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface CardItemProps {
  card: EditorCard;
  sectionType: 'listings-videos' | 'custom-links';
  onUpdate: (id: string, updates: Partial<EditorCard>) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.DragEvent, cardId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetCardId: string) => void;
  isDragging: boolean;
}
export function CardItem({
  card,
  sectionType,
  onUpdate,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging
}: CardItemProps) {
  const [showImageModal, setShowImageModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusOptions = ['Active', 'Coming Soon', 'Open House', 'Contingent', 'Closed', 'Pending'];
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      const url = URL.createObjectURL(file);
      onUpdate(card.id, {
        image: url
      });
      setShowImageModal(false);
    }
  };
  const handleRemoveImage = () => {
    onUpdate(card.id, {
      image: ''
    });
    setShowImageModal(false);
  };
  return <div draggable onDragStart={e => onDragStart(e, card.id)} onDragOver={onDragOver} onDrop={e => onDrop(e, card.id)} className={`flex items-start gap-3 p-4 bg-white border border-brand-light rounded-xl shadow-sm group transition-all ${isDragging ? 'opacity-50' : ''}`} data-id="element-53">
      <button type="button" className="mt-2 text-brand-light group-hover:text-brand-muted cursor-grab active:cursor-grabbing transition-colors" data-id="element-54">
        <GripVertical size={18} data-id="element-55" />
      </button>

      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-brand-offwhite border border-brand-light relative group/image cursor-pointer" onClick={() => setShowImageModal(true)} data-id="element-56">
        {card.image ? <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-57" /> : <div className="w-full h-full flex items-center justify-center text-brand-light" data-id="element-58">
            <ImageIcon size={20} data-id="element-59" />
          </div>}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-opacity" data-id="element-60">
          <span className="text-[8px] text-white font-medium" data-id="element-61">Edit</span>
        </div>
      </div>

      <div className="flex-1 space-y-2 min-w-0" data-id="element-62">
        <input type="text" value={card.title} onChange={e => onUpdate(card.id, {
        title: e.target.value
      })} className="w-full font-medium text-brand-dark outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Title" data-id="element-63" />
        <input type="text" value={card.url} onChange={e => onUpdate(card.id, {
        url: e.target.value
      })} className="w-full text-sm text-brand-muted outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors font-mono" placeholder="URL" data-id="element-64" />
        <input type="text" value={card.description} onChange={e => onUpdate(card.id, {
        description: e.target.value
      })} className="w-full text-sm text-brand-muted outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Description" data-id="element-65" />

        {sectionType === 'listings-videos' && <div className="flex items-center gap-2 pt-1" data-id="element-66">
            <input type="text" value={card.price || ''} onChange={e => onUpdate(card.id, {
          price: e.target.value
        })} className="w-24 text-sm font-mono font-medium text-brand-dark outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Price" data-id="element-67" />
            <select value={card.status || ''} onChange={e => onUpdate(card.id, {
          status: e.target.value
        })} className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 outline-none cursor-pointer" data-id="element-68">
              <option value="" data-id="element-69">No Status</option>
              {statusOptions.map(status => <option key={status} value={status} data-id="element-70">
                  {status}
                </option>)}
            </select>
          </div>}
      </div>

      <div className="flex items-center gap-2 shrink-0" data-id="element-71">
        <button type="button" className={`w-10 h-6 rounded-full transition-colors relative ${card.enabled ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => onUpdate(card.id, {
        enabled: !card.enabled
      })} data-id="element-72">
          <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${card.enabled ? 'left-5' : 'left-1'}`} data-id="element-73"></div>
        </button>
        <button type="button" onClick={() => onDelete(card.id)} className="p-1.5 text-brand-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-74">
          <X size={16} data-id="element-75" />
        </button>
      </div>

      {showImageModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowImageModal(false)} data-id="element-76">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-77">
            <div className="flex items-center justify-between mb-4" data-id="element-78">
              <h3 className="text-lg font-medium text-brand-dark" data-id="element-79">
                Listing Image
              </h3>
              <button onClick={() => setShowImageModal(false)} className="p-1 text-brand-muted hover:text-brand-dark transition-colors" data-id="element-80">
                <X size={20} data-id="element-81" />
              </button>
            </div>

            <div className="space-y-3" data-id="element-82">
              <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg, image/png, image/webp" className="hidden" data-id="element-83" />

              <button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-xl border border-brand-light bg-white hover:border-brand-forest hover:bg-brand-sage/20 transition-colors" data-id="element-84">
                <div className="w-8 h-8 rounded-full bg-brand-offwhite flex items-center justify-center" data-id="element-85">
                  <Upload size={16} className="text-brand-forest" data-id="element-86" />
                </div>
                <span className="text-sm font-medium text-brand-dark" data-id="element-87">
                  Upload new image
                </span>
              </button>

              {card.image && <button onClick={handleRemoveImage} className="w-full flex items-center gap-3 p-3 rounded-xl border border-brand-light bg-white hover:border-red-500 hover:bg-red-50 transition-colors" data-id="element-88">
                  <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center" data-id="element-89">
                    <Trash2 size={16} className="text-red-500" data-id="element-90" />
                  </div>
                  <span className="text-sm font-medium text-red-600" data-id="element-91">
                    Remove image
                  </span>
                </button>}

              <p className="text-xs text-brand-muted text-center pt-2" data-id="element-92">
                JPG, PNG or WebP. Max size 2MB.
              </p>
            </div>
          </div>
        </div>}
    </div>;
}